import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';

import Box from '@classify/react-core-components/Box';
import { styled, useTheme } from '@classify/react-core-components/styles';

import SimpleHeader from 'Modules/main-header/variants/SimpleHeader';

interface Props {
  topStickyElementSelector?: string;
  children: ReactNode;
  onChangeVisibility?: (isVisible: boolean) => void;
  actions?: ReactNode;
  isDisabled?: boolean;
}

const StickyHeader = styled('div')(
  ({ theme: { palette, zIndex, transitions, shadows, breakpoints } }) => ({
    zIndex: zIndex.appBar + 1,
    position: 'fixed',
    bottom: 0,
    width: '100%',
    border: `1px solid ${palette.layout.line.divider}`,
    borderLeft: 'none',
    borderRight: 'none',

    transform: 'translateY(100%)',
    transition: transitions.create('transform', {
      duration: transitions.duration.standard,
    }),

    '&.isVisible': {
      transform: 'translateY(0)',
      boxShadow: shadows[8],
    },

    [breakpoints.up('md')]: {
      top: 0,
      bottom: 'auto',
      transform: 'translateY(-100%)',
    },
  })
);

export default function EnsureCtaVisible({
  topStickyElementSelector,
  children,
  onChangeVisibility = () => {},
  actions,
  isDisabled,
}: Props) {
  const [isCtaVisible, setIsCtaVisible] = useState(true);

  const theme = useTheme();
  const hasSmallWidth = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const headerElement = topStickyElementSelector
      ? document.querySelector(topStickyElementSelector)
      : null;
    const topOffset = headerElement?.getBoundingClientRect().height ?? 0;

    const options = {
      rootMargin: `-${topOffset}px 0px 0px 0px`,
      threshold: 0,
    };

    const callback: IntersectionObserverCallback = (entries) => {
      if (isDisabled) {
        return;
      }
      const isVisible = entries.some((element) => element.isIntersecting);
      setIsCtaVisible(isVisible);
      onChangeVisibility(isVisible);
    };

    const observer = new IntersectionObserver(callback, options);

    const targets = document.querySelectorAll('[data-main-cta]');
    targets.forEach((e) => observer.observe(e));

    return () => {
      observer.disconnect();
    };
  }, [isDisabled, onChangeVisibility, topStickyElementSelector]);

  if (isDisabled || !actions) {
    return children;
  }

  return (
    <>
      <StickyHeader className={isCtaVisible ? '' : 'isVisible'}>
        <SimpleHeader
          hasSmallLogo
          shouldHideLogo={hasSmallWidth}
          isBusiness
          moreContent={<Box sx={{ display: 'flex', gap: 2 }}>{actions}</Box>}
        />
      </StickyHeader>

      <div>{children}</div>
    </>
  );
}
